import { defineStore } from "pinia";
import { createBaseStoreRefactor } from "./baseStoreRefactor";

// Create base store configuration for contact categories
const contactCategoriesBaseStore = createBaseStoreRefactor({
  endpoint: "/api/v1/contact_categories",
  name: "ContactCategoriesStoreRefactor",
  objectName: "Contact Categorie",
  enableCache: true, // Enable caching since categories change infrequently
  actionCableChannel: null, // No real-time updates needed
  infiniteLoading: false, // Simple list, no pagination needed
});

export const useContactCategoriesStoreRefactor = defineStore(
  "contactCategoriesRefactor",
  {
    ...contactCategoriesBaseStore,

    getters: {
      ...contactCategoriesBaseStore.getters,

      /**
       * Get all contact categories (maps to state.data for compatibility)
       */
      categories: (state) => {
        return state.data || [];
      },

      /**
       * Get only active categories (for creating new contacts)
       */
      activeCategories: (state) => {
        return (state.data || []).filter((cat) => cat.active === true);
      },

      /**
       * Get contact category by ID
       */
      getCategoryById: (state) => (id) => {
        return state.data.find((category) => category.id == id);
      },

      /**
       * Get categories formatted for select options (all categories)
       */
      categoryOptions: (state) => {
        return (state.data || []).map((category) => ({
          title: category.name,
          value: category.id,
          active: category.active,
          icon: category.icon,
          color: category.color,
        }));
      },

      /**
       * Get only active categories as select options (for new contacts)
       */
      activeCategoryOptions: (state) => {
        return (state.data || [])
          .filter((cat) => cat.active === true)
          .map((category) => ({
            title: category.name,
            value: category.id,
            active: true, // Always true since we filter for active categories
            icon: category.icon,
            color: category.color,
          }));
      },

      /**
       * Get categories as simple select items (for forms)
       */
      selectItems: (state) => {
        return (state.data || []).map((category) => ({
          label: category.name,
          value: category.id,
          name: category.name,
          active: category.active,
        }));
      },

      /**
       * Build select options for editing a contact
       * Includes: all active categories + any currently selected inactive categories
       * @param {Array} currentCategoryIds - IDs of categories currently assigned to the contact
       */
      getEditOptions:
        (state) =>
        (currentCategoryIds = []) => {
          const currentIds = new Set(currentCategoryIds.map(Number));
          return (state.data || [])
            .filter((cat) => cat.active === true || currentIds.has(cat.id))
            .map((category) => ({
              title: category.name,
              value: category.id,
              active: category.active,
              icon: category.icon,
              color: category.color,
              disabled: !category.active, // Show but mark inactive ones
            }));
        },
    },

    actions: {
      ...contactCategoriesBaseStore.actions,

      // All CRUD methods are fully inherited from base store
      // Auto-generated messages use "Contact Categorie" from objectName
    },
  },
);
